import { useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  getAllProviders,
  getProfiles,
  getProviderOfficeExamRoom,
  getProviderOfficeLocation,
  getStaffProvierByUser,
  updateProvider,
} from 'store/slices/patientInfoSlice';
import { useAppDispatch } from 'store/hooks';
import {
  ProfileSetting,
  SelectionList,
  SubmitPayload,
  SubmitProfileDTO,
  UserLocation,
} from './userOffice.types';
import UserLocationSelector from './UserLocationSelector';
import OfficeSetting from './OfficeSettings';
import { ROUTE } from 'route/RouteEnums';
import { Page } from 'components/Page';
import { toast } from 'react-toastify';
import { StyledDialog } from 'theme/index';
import SyncIcon from '@mui/icons-material/Sync';
import { reloadAppointmentCacheAPI } from 'services/adminServices';

/* ---------------- CONSTANTS ---------------- */

export const DAYS_OF_WEEK = [
  { id: 1, label: 'Mon', value: 1 },
  { id: 2, label: 'Tue', value: 2 },
  { id: 3, label: 'Wed', value: 3 },
  { id: 4, label: 'Thu', value: 4 },
  { id: 5, label: 'Fri', value: 5 },
  { id: 6, label: 'Sat', value: 6 },
  { id: 0, label: 'Sun', value: 0 },
];

/* ---------------- HELPERS ---------------- */

const calculateEndTime = (start: string, duration: number) => {
  if (!start || !duration) return '';

  const [h, m] = start.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  d.setMinutes(d.getMinutes() + duration);

  return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/* ---------------- COMPONENT ---------------- */

export default function UserOfficeList() {
  const dispatch = useAppDispatch();

  const [officeList, setOfficeList] = useState<SelectionList[]>([]);
  const [profileList, setProfileList] = useState<SelectionList[]>([]);
  const [providerList, setProviderList] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [userProfileMap, setUserProfileMap] = useState<any>(null);
  const [globalProfileSettings, setGlobalProfileSettings] = useState<number[]>([]);
  const globalProfiles = profileList.filter((p) => globalProfileSettings.includes(p.id));
  const [userLocations, setUserLocations] = useState<UserLocation[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const hasGlobalProfiles = globalProfileSettings.length > 0;
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const hasLocationError = (loc: UserLocation) => !loc.locationId;
  const hasProfileError = (loc: UserLocation) => loc.profiles.some((p) => !p.profileId);
  const [editingContext, setEditingContext] = useState<{
    locIndex: number;
    profIndex: number;
  } | null>(null);

  const editingProfile =
    editingContext !== null
      ? userLocations[editingContext.locIndex]?.profiles[editingContext.profIndex]
      : null;

  const [initialProfileSnapshot, setInitialProfileSnapshot] = useState<ProfileSetting | null>(null);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(false);
  /**
   * True while the Sync Offices button pulls the office details from DrChrono.
   */
  const [syncingOffices, setSyncingOffices] = useState(false);
  /* ---------------- VALIDATION ---------------- */

  const selectedLocation = editingContext !== null ? userLocations[editingContext.locIndex] : null;
  const hasExamRoomError = (loc: UserLocation) =>
    !loc.defaultExamRoomId ||
    loc.profiles.some((p) => !p.secondaryExamRoomId && !loc.defaultExamRoomId);

  /* ---------------- API ---------------- */

  /**
   * Loads the offices and appointment profiles shown in the location / profile pickers.
   */
  const loadOfficesAndProfiles = () =>
    Promise.all([
      dispatch(getProfiles({}))
        .unwrap()
        .then((r: any) => setProfileList(r.data ?? [])),
      dispatch(getProviderOfficeLocation({}))
        .unwrap()
        .then((r: any) => setOfficeList(r.data ?? [])),
    ]);

  useEffect(() => {
    loadOfficesAndProfiles();
    dispatch(getAllProviders())
      .unwrap()
      .then((r: any) => setProviderList(r.data ?? []));
  }, []);

  /**
   * Sync Offices button: ehr-sync pulls the office details from DrChrono and stores them in its DB only (reloadCache);
   * its in-memory office cache is not cleared - every instance evicts and rebuilds it in the daily early-morning job
   * (CT), so offices just added in DrChrono show in these lists after that run (user decision, 2026-10-29).
   */
  const handleSyncOffices = async () => {
    setSyncingOffices(true);
    try {
      await reloadAppointmentCacheAPI();
      toast('Office sync completed successfully.', { type: 'success', icon: true });
    } catch {
      toast('Office sync failed. Please try again.', { type: 'error', icon: true });
    } finally {
      setSyncingOffices(false);
    }
  };

  const fetchStaffProviderByUser = async (userId: number | null) => {
    if (!userId) return;

    try {
      setLoadingUserData(true);
      const res = await dispatch(getStaffProvierByUser(userId)).unwrap();
      if (res?.status === 200) {
        const provider = res.data ?? null;
        setUserProfileMap(provider);
        setGlobalProfileSettings(provider?.globalProfileSettings ?? []);
      }
    } finally {
      setLoadingUserData(false); // ✅ always stop loader
    }
  };

  useEffect(() => {
    fetchStaffProviderByUser(selectedUser);
  }, [selectedUser]);
  useEffect(() => {
    if (!userProfileMap) return;

    const locations = userProfileMap.locations ?? {};

    const mapped: UserLocation[] = Object.keys(locations).map((locId) => {
      const schedules = locations[locId];

      // ✅ Correct extraction from nested examRoom object
      const firstProfileWithRoom = schedules.find(
        (s: any) =>
          s.examRoom &&
          s.examRoom.defaultExamRoomId !== null &&
          s.examRoom.defaultExamRoomId !== undefined
      );

      const derivedDefaultRoom = firstProfileWithRoom
        ? Number(firstProfileWithRoom.examRoom.defaultExamRoomId)
        : '';

      return {
        locationId: Number(locId),

        // ✅ Now this will be correctly set
        defaultExamRoomId: derivedDefaultRoom,

        examRooms: [],

        profiles: schedules.map((s: any) => ({
          profileId: s.profileId,

          // ✅ Secondary override logic
          secondaryExamRoomId: Number(s.examRoom?.secondaryExamRoomId)
            ? Number(s.examRoom?.secondaryExamRoomId)
            : s.examRoom?.derivedDefaultRoom,

          scheduleTime: s.scheduleTime,
          scheduleEndTime: s.scheduleEndTime,
          duration: s.duration,
          weekDays: s.weekDays ?? [],
          isEditing: false,
        })),
      };
    });

    setUserLocations(mapped);

    // fetch exam rooms same as before
    mapped.forEach((loc, index) => {
      if (loc.locationId) {
        dispatch(getProviderOfficeExamRoom({ officeId: loc.locationId }))
          .unwrap()
          .then((res: any) => {
            const normalized = (res.data ?? []).map((r: any) => ({
              id: r.index,
              name: r.name,
            }));

            setUserLocations((prev) =>
              prev.map((l, i) => (i === index ? { ...l, examRooms: normalized } : l))
            );
          });
      }
    });
  }, [userProfileMap]);

  /* ---------------- SAFE UPDATE ---------------- */

  const updateProfile = (
    locIndex: number,
    profIndex: number,
    updater: (p: ProfileSetting) => ProfileSetting
  ) => {
    setUserLocations((prev) =>
      prev.map((l, li) =>
        li !== locIndex
          ? l
          : {
              ...l,
              profiles: l.profiles.map((p, pi) => (pi !== profIndex ? p : updater({ ...p }))),
            }
      )
    );
  };

  const buildSubmitPayload = () => {
    const locationMap: Record<string, any[]> = {};

    userLocations.forEach((loc) => {
      if (!loc.profiles || loc.profiles.length === 0) return;

      locationMap[loc.locationId] = loc.profiles.map((p) => ({
        profileId: p.profileId,
        duration: p.duration,
        scheduleTime: p.scheduleTime,
        scheduleEndTime: p.scheduleEndTime,
        weekDays: p.weekDays ?? [],
        examRoom: {
          defaultExamRoomId: loc.defaultExamRoomId, // 🔥 fallback to location default if profile override is empty
          secondaryExamRoomId: p.secondaryExamRoomId
            ? p.secondaryExamRoomId
            : loc.defaultExamRoomId,
        },
      }));
    });

    return {
      userId: selectedUser,
      locations: locationMap,
    };
  };
  const handleSubmit = () => {
    setSubmitAttempted(true);
    const hasErrors = userLocations.some(
      (loc) =>
        hasLocationError(loc) ||
        !loc.defaultExamRoomId ||
        hasProfileError(loc) ||
        hasExamRoomError(loc)
    );

    if (hasErrors) {
      return; // ❌ block submit
    }

    const payload = buildSubmitPayload();
    dispatch(updateProvider(payload))
      .unwrap()
      .then((r: any) => {
        toast('User office settings updated successfully.', {
          type: 'success',
          icon: true,
        });
        setUserProfileMap(r.data);
      })
      .catch(() => {
        toast('Failed to update user office settings. Please try again.', {
          type: 'error',
          icon: true,
        });
      });
  };

  return (
    <Page backDir={ROUTE.ADMIN_PRACTICE_MANAGE_USERS_LIST} buttonText='Back'>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loadingUserData}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container component='main' maxWidth='md'>
        <Grid container spacing={2}>
          <Grid item xs={8} display={'flex'} alignItems={'center'} mt={3} mb={3}>
            <Typography variant='h5' gutterBottom>
              Provider Locations
            </Typography>
          </Grid>
          <Grid item xs={4} display={'flex'} alignItems={'center'} justifyContent={'flex-end'} mt={3} mb={3}>
            <Button
              variant='outlined'
              sx={{ textTransform: 'capitalize' }}
              startIcon={
                syncingOffices ? <CircularProgress size={18} color='inherit' /> : <SyncIcon />
              }
              disabled={syncingOffices}
              onClick={handleSyncOffices}
            >
              Sync Offices
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              borderRadius: 3,
              mb: 2,
            }}
          >
            <TextField
              select
              fullWidth
              label='Provider'
              value={selectedUser ?? ''}
              onChange={(e) => {
                setSelectedUser(Number(e.target.value));
                setUserLocations([]);
                setLoadingUserData(true);
              }}
            >
              <MenuItem value='' disabled>
                Select Provider
              </MenuItem>

              {providerList.map((p) => (
                <MenuItem key={p.userId} value={p.userId}>
                  {p.firstName} {p.lastName}
                </MenuItem>
              ))}
            </TextField>

            {selectedUser && hasGlobalProfiles && (
              <UserLocationSelector
                value={userLocations}
                locations={officeList}
                submitAttempted={submitAttempted}
                profiles={globalProfiles}
                onChange={setUserLocations}
                onEditProfile={(l, p) => {
                  setInitialProfileSnapshot(
                    JSON.parse(JSON.stringify(userLocations[l].profiles[p]))
                  );
                  setEditingContext({ locIndex: l, profIndex: p });
                  setOpenModal(true);
                }}
              />
            )}
            {selectedUser && !hasGlobalProfiles && (
              <FormHelperText sx={{ color: 'error.main', mt: 2, textAlign: 'center' }}>
                No appointment templates selected for this user
              </FormHelperText>
            )}

            {selectedUser && hasGlobalProfiles && (
              <Box mt={4} textAlign='center'>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleSubmit}
                  disabled={!selectedUser}
                >
                  Submit
                </Button>
              </Box>
            )}
            <StyledDialog open={openModal} maxWidth='md' fullWidth>
              <DialogTitle>Office Settings</DialogTitle>

              <DialogContent dividers>
                {editingProfile && editingContext && selectedLocation && (
                  <OfficeSetting
                    examRoomId={
                      editingProfile.secondaryExamRoomId !== undefined &&
                      editingProfile.secondaryExamRoomId !== ''
                        ? editingProfile.secondaryExamRoomId
                        : selectedLocation.defaultExamRoomId
                    }
                    examRooms={selectedLocation.examRooms ?? []}
                    onExamRoomChange={(id) =>
                      updateProfile(editingContext.locIndex, editingContext.profIndex, (p) => ({
                        ...p,
                        secondaryExamRoomId: id,
                      }))
                    }
                    startTime={editingProfile.scheduleTime ?? ''}
                    duration={String(editingProfile.duration || '')}
                    endTime={editingProfile.scheduleEndTime ?? ''}
                    daysOfWeek={DAYS_OF_WEEK}
                    selectedWeekDays={editingProfile.weekDays ?? []}
                    onStartTimeChange={(v) =>
                      updateProfile(editingContext.locIndex, editingContext.profIndex, (p) => ({
                        ...p,
                        scheduleTime: v,
                        scheduleEndTime: calculateEndTime(v, p.duration || 0),
                      }))
                    }
                    onDurationChange={(v) =>
                      updateProfile(editingContext.locIndex, editingContext.profIndex, (p) => ({
                        ...p,
                        duration: Number(v),
                        scheduleEndTime: calculateEndTime(p.scheduleTime ?? '', Number(v)),
                      }))
                    }
                    onWeekDayToggle={(d) =>
                      updateProfile(editingContext.locIndex, editingContext.profIndex, (p) => ({
                        ...p,
                        weekDays: p.weekDays?.includes(d)
                          ? p.weekDays.filter((x) => x !== d)
                          : [...(p.weekDays ?? []), d],
                      }))
                    }
                    submitAttempted={submitAttempted}
                  />
                )}
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' onClick={() => setOpenModal(false)}>
                  Cancel
                </Button>
                <Button variant='contained' onClick={() => setOpenModal(false)}>
                  Save
                </Button>
              </DialogActions>
            </StyledDialog>
          </Paper>
        </Grid>
      </Container>
    </Page>
  );
}
